import { useState, useRef, useEffect } from "react";

type DatePrecision = "year" | "exact";

interface DatePickerProps {
  label: string;
  precision: DatePrecision;
  onPrecisionChange: (p: DatePrecision) => void;
  /** YYYY or YYYY-MM-DD */
  value: string;
  onChange: (val: string) => void;
  disabled?: boolean;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

const currentYear = new Date().getFullYear();

function daysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

type View = "closed" | "years" | "months" | "days";

export function DatePicker({
  label,
  precision,
  onPrecisionChange,
  value,
  onChange,
  disabled,
}: DatePickerProps) {
  const [view, setView] = useState<View>("closed");
  const [pageStart, setPageStart] = useState(() => {
    const y = value ? parseInt(value.slice(0, 4), 10) : currentYear;
    return Math.floor((isNaN(y) ? currentYear : y) / 16) * 16;
  });
  const [selectedYear, setSelectedYear] = useState(() => {
    const y = value ? parseInt(value.slice(0, 4), 10) : NaN;
    return isNaN(y) ? null : y;
  });
  const [selectedMonth, setSelectedMonth] = useState<number | null>(() => {
    if (value && value.length >= 7) {
      const m = parseInt(value.slice(5, 7), 10);
      return isNaN(m) ? null : m - 1;
    }
    return null;
  });

  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (view === "closed") return;
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setView("closed");
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [view]);

  function displayValue(): string {
    if (!value) return "";
    if (value.length === 4) return value;
    // YYYY-MM-DD
    const d = new Date(value + "T00:00:00");
    return d.toLocaleDateString("en-GB", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  function openPicker() {
    if (disabled) return;
    if (precision === "year") {
      setView("years");
    } else {
      // If we already have a year selected, go to months; otherwise years
      setView(selectedYear ? "months" : "years");
    }
  }

  function handleYearClick(y: number) {
    setSelectedYear(y);
    if (precision === "year") {
      onChange(String(y));
      setView("closed");
    } else {
      setView("months");
    }
  }

  function handleMonthClick(m: number) {
    setSelectedMonth(m);
    setView("days");
  }

  function handleDayClick(d: number) {
    if (selectedYear == null || selectedMonth == null) return;
    const mm = String(selectedMonth + 1).padStart(2, "0");
    const dd = String(d).padStart(2, "0");
    onChange(`${selectedYear}-${mm}-${dd}`);
    setView("closed");
  }

  function clear() {
    onChange("");
    setSelectedYear(null);
    setSelectedMonth(null);
    setView("closed");
  }

  // Year grid: 4 columns × 4 rows = 16 years per page
  const yearGridStart = pageStart;
  const yearGridEnd = pageStart + 15;
  const yearGrid: number[] = [];
  for (let y = yearGridStart; y <= yearGridEnd; y++) yearGrid.push(y);

  // Days grid
  const daysCount =
    selectedYear != null && selectedMonth != null
      ? daysInMonth(selectedYear, selectedMonth)
      : 0;
  const firstDayOfWeek =
    selectedYear != null && selectedMonth != null
      ? new Date(selectedYear, selectedMonth, 1).getDay()
      : 0;
  // Adjust so Monday = 0
  const startOffset = (firstDayOfWeek + 6) % 7;

  const panelStyle: React.CSSProperties = {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    zIndex: 100,
    marginTop: 4,
    background: "var(--c-bg)",
    border: "1px solid var(--c-border-strong)",
    borderRadius: 12,
    boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
    padding: 16,
    maxHeight: "80vh",
    overflowY: "auto",
  };

  const headerStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  };

  const navBtnStyle: React.CSSProperties = {
    background: "none",
    border: "1px solid var(--c-border)",
    borderRadius: 8,
    width: 36,
    height: 36,
    minHeight: 36,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: "1.1rem",
    color: "var(--c-text)",
    padding: 0,
  };

  const cellStyle = (isSelected: boolean): React.CSSProperties => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 44,
    borderRadius: 8,
    cursor: "pointer",
    fontWeight: 700,
    fontSize: "0.95rem",
    background: isSelected ? "var(--c-blue, #0058a3)" : "transparent",
    color: isSelected ? "#fff" : "var(--c-text)",
    border: "none",
    transition: "background 0.15s, color 0.15s",
  });

  const gridStyle = (cols: number): React.CSSProperties => ({
    display: "grid",
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    gap: 4,
  });

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>{label}</label>

      {/* Precision toggle */}
      <div style={{
        display: "inline-flex", borderRadius: 8, overflow: "hidden",
        border: "1px solid var(--c-border-strong)", marginBottom: 8,
      }}>
        {(["year", "exact"] as const).map((p) => (
          <button key={p} type="button" disabled={disabled}
            onClick={() => {
              onPrecisionChange(p);
              if (p === "year" && value && value.length > 4) {
                // Keep just the year
                onChange(value.slice(0, 4));
              }
              setView("closed");
            }}
            style={{
              padding: "6px 14px", border: "none", cursor: "pointer",
              fontSize: "0.85rem", fontWeight: precision === p ? 700 : 400,
              background: precision === p ? "var(--c-blue, #0058a3)" : "var(--c-bg)",
              color: precision === p ? "#fff" : "var(--c-text)",
              transition: "background 0.15s, color 0.15s",
            }}>
            {p === "year" ? "Year only" : "Exact date"}
          </button>
        ))}
      </div>

      {/* Display / trigger */}
      <div
        onClick={openPicker}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "10px 14px", border: "1px solid var(--c-border-strong)",
          borderRadius: 8, cursor: disabled ? "default" : "pointer",
          background: "var(--c-bg)", minHeight: 44,
          opacity: disabled ? 0.5 : 1,
        }}>
        <span style={{ color: value ? "var(--c-text)" : "var(--c-text-subtle)", fontSize: "0.95rem" }}>
          {displayValue() || (precision === "year" ? "Select year…" : "Select date…")}
        </span>
        <span style={{ display: "flex", gap: 4 }}>
          {value && !disabled && (
            <button type="button" onClick={(e) => { e.stopPropagation(); clear(); }}
              style={{
                background: "none", border: "none", cursor: "pointer",
                color: "var(--c-text-subtle)", fontSize: "1rem", padding: "0 4px",
              }}
              aria-label="Clear">
              ✕
            </button>
          )}
          <span style={{ color: "var(--c-text-subtle)", fontSize: "1.1rem" }}>📅</span>
        </span>
      </div>

      {/* Year picker panel */}
      {view === "years" && (
        <div style={panelStyle}>
          <div style={headerStyle}>
            <button type="button" style={navBtnStyle}
              onClick={() => setPageStart((s) => s - 16)}>‹</button>
            <span style={{ fontWeight: 700, fontSize: "0.95rem" }}>
              {yearGridStart} – {yearGridEnd}
            </span>
            <button type="button" style={navBtnStyle}
              onClick={() => setPageStart((s) => Math.min(s + 16, currentYear - 15 + 16))}>›</button>
          </div>
          <div style={gridStyle(4)}>
            {yearGrid.map((y) => (
              <button key={y} type="button"
                style={cellStyle(y === selectedYear)}
                onMouseEnter={(e) => {
                  if (y !== selectedYear) e.currentTarget.style.background = "var(--c-bg-alt)";
                }}
                onMouseLeave={(e) => {
                  if (y !== selectedYear) e.currentTarget.style.background = "transparent";
                }}
                onClick={() => handleYearClick(y)}>
                {y}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Month picker panel */}
      {view === "months" && selectedYear != null && (
        <div style={panelStyle}>
          <div style={headerStyle}>
            <button type="button" style={navBtnStyle}
              onClick={() => { setSelectedYear((y) => (y ?? currentYear) - 1); }}>‹</button>
            <span style={{ fontWeight: 700, fontSize: "0.95rem", cursor: "pointer" }}
              onClick={() => setView("years")}>
              {selectedYear}
            </span>
            <button type="button" style={navBtnStyle}
              onClick={() => { setSelectedYear((y) => (y ?? currentYear) + 1); }}>›</button>
          </div>
          <div style={gridStyle(4)}>
            {MONTHS.map((m, i) => (
              <button key={m} type="button"
                style={cellStyle(i === selectedMonth)}
                onMouseEnter={(e) => {
                  if (i !== selectedMonth) e.currentTarget.style.background = "var(--c-bg-alt)";
                }}
                onMouseLeave={(e) => {
                  if (i !== selectedMonth) e.currentTarget.style.background = "transparent";
                }}
                onClick={() => handleMonthClick(i)}>
                {m}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Day picker panel */}
      {view === "days" && selectedYear != null && selectedMonth != null && (
        <div style={panelStyle}>
          <div style={headerStyle}>
            <button type="button" style={navBtnStyle}
              onClick={() => {
                if (selectedMonth === 0) {
                  setSelectedMonth(11);
                  setSelectedYear((y) => (y ?? currentYear) - 1);
                } else {
                  setSelectedMonth((m) => (m ?? 0) - 1);
                }
              }}>‹</button>
            <span style={{ fontWeight: 700, fontSize: "0.95rem", cursor: "pointer" }}
              onClick={() => setView("months")}>
              {MONTHS[selectedMonth]} {selectedYear}
            </span>
            <button type="button" style={navBtnStyle}
              onClick={() => {
                if (selectedMonth === 11) {
                  setSelectedMonth(0);
                  setSelectedYear((y) => (y ?? currentYear) + 1);
                } else {
                  setSelectedMonth((m) => (m ?? 0) + 1);
                }
              }}>›</button>
          </div>
          {/* Day-of-week headers */}
          <div style={{ ...gridStyle(7), marginBottom: 4 }}>
            {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
              <div key={d} style={{
                textAlign: "center", fontSize: "0.75rem", fontWeight: 600,
                color: "var(--c-text-subtle)", padding: "4px 0",
              }}>{d}</div>
            ))}
          </div>
          <div style={gridStyle(7)}>
            {/* Empty cells for offset */}
            {Array.from({ length: startOffset }).map((_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {Array.from({ length: daysCount }).map((_, i) => {
              const day = i + 1;
              const mm = String(selectedMonth + 1).padStart(2, "0");
              const dd = String(day).padStart(2, "0");
              const isSelected = value === `${selectedYear}-${mm}-${dd}`;
              return (
                <button key={day} type="button"
                  style={cellStyle(isSelected)}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.background = "var(--c-bg-alt)";
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.background = "transparent";
                  }}
                  onClick={() => handleDayClick(day)}>
                  {day}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
